import ServiceAccount from "@/services/v2/data/service-account/ServiceAccount";
import RouterService from "@/services/RouterService";
import AppController from "@/targets/light/components/App/ts/AppController";
import {computed, ComputedRef, nextTick, Ref, ref} from "vue";
import ModelAccount from "@models/v2/account/ModelAccount";
import PurchaseOperation from "@models/operations/PurchaseOperation";
import {PaymentFlow, PaymentFlowRange, PaymentFlowRanges} from "@/targets/light/types";
import ModelAgent from "@models/agents/ModelAgent";
import ServiceAgents from "@/services/v2/data/service-agents/ServiceAgents";
import ServiceRanges from "@/services/v2/data/service-ranges/ServiceRanges";
import ModelLightRanges from "@models/v2/ranges/ModelLightRanges";
import LightPaymentService from "@/targets/light/services/LightPaymentService";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import DocumentPurchase from "@/services/firebase/firestore/documents/DocumentPurchase";
import {DocumentTypes} from "@/services/firebase/firestore/documents/DocumentTypes";
import {CreateLightPaymentPayload} from "@/services/operations/payment/PaymentService";
import {f7} from "framework7-vue";
import {ServiceCoreApiResponseError} from "@/services/v2/service-core-api/errors/ServiceCoreApiResponseError";

export default class CreatePaymentViewController {
	private static _instance: CreatePaymentViewController | null = null;
	private _appController: AppController = AppController.of();
	private _paymentService: LightPaymentService = LightPaymentService.of();
	private _serviceAccount: ServiceAccount = ServiceAccount.of();
	private _serviceAgents: ServiceAgents = ServiceAgents.of();
	private _serviceRanges: ServiceRanges = ServiceRanges.of();
	private _routerService: RouterService = this._appController.routerService;
	private _flowRanges = ref<ModelLightRanges | null>(null);

	private _isCanSelectAgent = ref<boolean>(Boolean(import.meta.env.VITE_PURCHASE_IS_CAN_SELECT_AGENT));
	private _minAmountEnabled = ref<boolean>(Boolean(import.meta.env.VITE_PURCHASE_MIN_AMOUNT_ENABLED));
	private _minAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_MIN_AMOUNT));
	private _defaultAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_DEFAULT_AMOUNT));
	private _defaultExtWFlowAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_DEFAULT_EXT_W_FLOW_AMOUNT));

	public payment: ComputedRef<PurchaseOperation | null>;
	public paymentError: Ref<ServiceCoreApiResponseError | null>
	public account: Ref<ModelAccount | null>;
	public inputAmount = ref<number>(0);
	public selectedPaymentFlow = ref<PaymentFlow | null>(null);
	public selectedAgent: ComputedRef<ModelAgent | null>;
	public calculatedFlowRangesByFirebase: ComputedRef<PaymentFlowRanges | null>;
	public calculatedRangesForBlockingAgents: ComputedRef<PaymentFlowRange | null>;
	public minMaxRangesBySelectedFlow: ComputedRef<PaymentFlowRange | null>;
	public isInputAmountDirty = ref<boolean>(false);
	public isInputAmountError: ComputedRef<boolean>;
	public isPaymentFlowError: ComputedRef<boolean>;

	public isNextButtonLoading = ref<boolean>(false);
	public isFlowRangesLoading = ref<boolean>(false);

	public currency: ComputedRef<"RUB" | "USD" | "USDT" | "KZT">;
	public suggestedAmounts: ComputedRef<number[]>;

	constructor() {
		this.payment = computed(() => this._paymentService.payment.value);
		this.paymentError = ref(null);
		this.account = this._serviceAccount.account;
		this.inputAmount.value = this.payment.value?.amount ?? 0;

		this.currency = computed(() => this.account.value?.currency?.name ?? "RUB");

		this.suggestedAmounts = computed(() => {
			return this.account.value?.currency.dSums ?? [1500, 3000, 5000, 7000];
		});

		this.selectedAgent = computed(() => this._serviceAgents.selectedPurchaseAgent.value);

		this.calculatedRangesForBlockingAgents = computed(() => {
			if (this._flowRanges.value) {
				const total: PaymentFlowRange = {
					min: this._flowRanges.value.total.min,
					max: this._flowRanges.value.total.max,
				}

				return this.calculateMinMaxAmountRangesByFirebase(total)
			}

			return null;
		});

		this.calculatedFlowRangesByFirebase = computed(() => {
			if (this._flowRanges.value) {
				const total: PaymentFlowRange = this.selectedAgent.value
					? {
						min: this.selectedAgent.value.sumRanges.currency.min ?? 0,
						max: this.selectedAgent.value.sumRanges.currency.max ?? 0,
					}
					: {
						min: this._flowRanges.value.total.min,
						max: this._flowRanges.value.total.max,
					};

				return {
					card: this.calculateMinMaxAmountRangesByFirebase(this._flowRanges.value.card),
					sbp: this.calculateMinMaxAmountRangesByFirebase(this._flowRanges.value.sbp),
					total: this.calculateMinMaxAmountRangesByFirebase(total),
				}
			}

			return null;
		});

		this.minMaxRangesBySelectedFlow = computed(() => {
			if (this.calculatedFlowRangesByFirebase.value && this.selectedPaymentFlow.value) {
				return this.calculatedFlowRangesByFirebase.value[this.selectedPaymentFlow.value];
			}

			return null;
		});

		this.isInputAmountError = computed(() =>
			this.isInputAmountDirty.value && this.selectedPaymentFlow.value === null
		);

		this.isPaymentFlowError = computed(() => {
			if (!this.isInputAmountDirty.value || !this.selectedPaymentFlow.value || !this.minMaxRangesBySelectedFlow.value) {
				return false;
			}

			const { min, max } = this.minMaxRangesBySelectedFlow.value;
			return this.inputAmount.value < min || this.inputAmount.value > max;
		});
	}

	private calculateMinMaxAmountRangesByFirebase(ranges: PaymentFlowRange) {
		if (this.account.value) {
			const tmpRanges: PaymentFlowRange = {
				min: ranges.min,
				max: ranges.max,
			}

			const { buy } = this.account.value.currency.rates;
			const minAmount = this._minAmount.value * buy;

			if (minAmount > tmpRanges.min) {
				tmpRanges.min = Math.min(minAmount, tmpRanges.max);
			}

			if (tmpRanges.min === tmpRanges.max) return null;

			if (tmpRanges.min >= tmpRanges.max) return null;

			return {
				min: Math.ceil(tmpRanges.min),
				max: Math.floor(tmpRanges.max),
			};
		}

		return null;
	}

	public get routerService(): RouterService {
		return this._routerService;
	}

	public get paymentService(): LightPaymentService {
		return this._paymentService;
	}

	private setPurchaseFirebaseConfig() {
		const doc = FirebaseService.of().firestoreService?.getDocument<DocumentPurchase>(DocumentTypes.PURCHASE);
		if (doc) {
			if (doc.isCanSelectAgent) {
				this._isCanSelectAgent.value = doc.isCanSelectAgent!;
			}

			if (doc.minAmountEnabled) {
				this._minAmountEnabled.value = doc.minAmountEnabled;
			}

			if (doc.defaultAmount !== null) {
				this._defaultAmount.value = doc.defaultAmount;
			}

			if (doc.minAmount !== null) {
				if (this._minAmountEnabled.value) {
					this._minAmount.value = doc.minAmount;
				}
			}

			if (doc.defaultExtWFlowAmount) {
				this._defaultExtWFlowAmount.value = doc.defaultExtWFlowAmount
			}
		}
	}

	private calculateStartAmount() {
		this.setPurchaseFirebaseConfig();

		if (this.account.value) {
			if (this.payment.value && this.payment.value.amount && this.payment.value.amount > 0) {
				this.inputAmount.value = this.payment.value.amount;
			} else {
				const { buy } = this.account.value.currency.rates;
				this.inputAmount.value = Math.ceil(this._defaultAmount.value * buy);
			}
		}

		return 0;
	}

	private async setUpSuitablePaymentFlow() {
		await this.fetchFlowRanges();

		const ranges = this.calculatedFlowRangesByFirebase.value;

		if (ranges) {
			if (ranges.card && this.isAmountBetweenFlowRanges(this.inputAmount.value, ranges.card)) {
				this.selectedPaymentFlow.value = PaymentFlow.CARD;
				return;
			}

			if (ranges.sbp && this.isAmountBetweenFlowRanges(this.inputAmount.value, ranges.sbp)) {
				this.selectedPaymentFlow.value = PaymentFlow.SBP;
				return;
			}

			if (ranges.total && this.isAmountBetweenFlowRanges(this.inputAmount.value, ranges.total)) {
				this.selectedPaymentFlow.value = PaymentFlow.AGENT;
				return;
			}

			this.isInputAmountDirty.value = true;
		}
	}

	private isAmountBetweenFlowRanges(amount: number, ranges: PaymentFlowRange) {
		return amount >= ranges.min && amount <= ranges.max;
	}

	private async fetchFlowRanges() {
		try {
			this.isFlowRangesLoading.value = true;
			this._flowRanges.value = await this._serviceRanges.fetchPurchaseLightRanges();
		} catch (e) {
			console.log(e);
		} finally {
			this.isFlowRangesLoading.value = false;
		}
	}

	public async next() {
		this.calculateStartAmount();
		await this.setUpSuitablePaymentFlow();
		this.startTimer();
	}

	public startTimer() {
		if (this.payment.value) {
			this._paymentService.timerService.start(this.payment.value.status!, this.payment.value.updatedAt!);
			this._paymentService.timerService.setCallback(this._paymentService.updatePayment.bind(this._paymentService));
		}
	}

	public async handleSubmit() {
		this.isInputAmountDirty.value = true;

		try {
			this.isNextButtonLoading.value = true;

			if (this.validateInputAmount()) {
				await this.createPaymentProcess();
				f7.views.main.router.navigate('/payment');
			}
		} catch (e) {
			throw e;
		} finally {
			this.isNextButtonLoading.value = false;
		}
	}

	private validateInputAmount() {
		return !(this.isInputAmountError.value || this.isPaymentFlowError.value);
	}

	private async createPaymentProcess() {
		const payload: CreateLightPaymentPayload = {
			amount: this.inputAmount.value,
			msid: this.payment.value!.msid!,
			payment_method:
				this.selectedPaymentFlow.value === PaymentFlow.CARD ?
					'bank_card' :
					this.selectedPaymentFlow.value === PaymentFlow.SBP ?
						'phone' : undefined,
			account_ids: this.selectedAgent.value ? [this.selectedAgent.value.id] : undefined,
		}

		await this._paymentService.patchEmptyPayment(payload);
		FirebaseService.of().wrongRangeMetricSuccess.checkAndSendAnalyticEvent();
		FirebaseService.of().emptyRangeMetricSuccess.checkAndSendAnalyticEvent();
	}

	public resetAgent() {
		this._serviceAgents.selectedPurchaseAgent = null;
	}

	public async setPaymentFlow(flow: PaymentFlow) {
		this.selectedPaymentFlow.value = flow;
		this.setCorrectlyInputAmountByFlow();
	}

	private setCorrectlyInputAmountByFlow() {
		if (!this.minMaxRangesBySelectedFlow.value) return;

		const { min, max } = this.minMaxRangesBySelectedFlow.value;
		this.inputAmount.value = Math.min(Math.max(this.inputAmount.value, min), max);
	}

	public returnToSite() {
		if (this.payment.value && this.payment.value.successUrl) {
			window.location.href = this.payment.value.successUrl;
		}
	}

	public static of() {
		if (CreatePaymentViewController._instance === null) {
			CreatePaymentViewController._instance = new CreatePaymentViewController();
		}
		return CreatePaymentViewController._instance;
	}
}
